import { Dispatch, SetStateAction } from 'react';
import SharedToolbar from '../../../shared/shared-toolbar-latest';
import { useUserSettings } from '../../../utils/use-user-settings';
import { ExtendedVaultExportModel } from './vault-export-grid';
import {
  isAbleToSeeUIElement,
  VaultRoles,
} from '../../../utils/roles/client-roles';
import { useQueryClient } from '@tanstack/react-query';
import { VaultKeys } from '../../../api/vault-api';
import CreateButton from '../../../shared/buttons/create-button';
import { getDeletePermissionForCorpus } from '../../../utils/vault-utils';
import SharedIconButton from '../../../shared/buttons/shared-icon-button';
import SharedGridStateIconButton from '../../../shared/shared-grid-state-icon-button-latest';
import { ModuleList } from '../../../shared/analytics/module-list';
import { Messages } from '../../../utils/message-constants';

interface VaultExportToolbarProps {
  matterId?: string;
  rows: ExtendedVaultExportModel[];
  setOpenAddVaultExport: Dispatch<SetStateAction<boolean>>;
  setOpenDeleteVaultExports: Dispatch<SetStateAction<boolean>>;
  selectedExports: string[];
}

export default function VaultExportToolbar({
  matterId = '',
  rows,
  setOpenAddVaultExport,
  setOpenDeleteVaultExports,
  selectedExports,
}: VaultExportToolbarProps) {
  const { toolsRoles } = useUserSettings();
  const queryClient = useQueryClient();
  const canCreateVaultExport =
    isAbleToSeeUIElement(toolsRoles, VaultRoles.CreateVaultExports.value) ||
    [
      VaultRoles.CreateGmailVaultExport,
      VaultRoles.CreateChatVaultExport,
      VaultRoles.CreateGeminiVaultExport,
      VaultRoles.CreateCalendarVaultExport,
      VaultRoles.CreateDriveVaultExport,
      VaultRoles.CreateGroupsVaultExport,
    ].some((permission) => isAbleToSeeUIElement(toolsRoles, permission.value));

  const hasGlobalDelete = isAbleToSeeUIElement(
    toolsRoles,
    VaultRoles.DeleteVaultExports.value
  );
  const canBulkDelete =
    selectedExports.length > 0 &&
    (hasGlobalDelete ||
      selectedExports.every((id) => {
        const row = rows.find((r) => r.id === id);
        if (!row) return false;
        const requiredPermission = getDeletePermissionForCorpus(row.corpus);
        return isAbleToSeeUIElement(toolsRoles, requiredPermission.value);
      }));

  return (
    <SharedToolbar
      quickFilterPlaceholder="Search..."
      secondaryButtons={
        <>
          {!!matterId && (
            <CreateButton
              disabled={!canCreateVaultExport}
              onClick={() => setOpenAddVaultExport(true)}
              label="Create Export"
              tooltipLabel={
                !canCreateVaultExport
                  ? Messages.Errors.Permission.Acting(
                      VaultRoles.CreateVaultExports.name
                    )
                  : ''
              }
            />
          )}

          <SharedIconButton
            variant="delete"
            disabled={!canBulkDelete}
            onClick={() => setOpenDeleteVaultExports(true)}
            tooltipLabel={
              selectedExports.length === 0
                ? Messages.Info.NothingSelected
                : !canBulkDelete && hasGlobalDelete
                ? Messages.Errors.Permission.Generic
                : !canBulkDelete
                ? Messages.Errors.Permission.Acting(
                    VaultRoles.DeleteVaultExports.name
                  )
                : 'Delete'
            }
          />

          <SharedIconButton
            variant="refresh"
            onClick={() => {
              if (matterId) {
                queryClient.invalidateQueries({
                  queryKey: [VaultKeys.VaultExports, matterId],
                });
              } else {
                queryClient.invalidateQueries({
                  queryKey: [VaultKeys.VaultExports],
                });
              }
            }}
            tooltipLabel="Refresh"
          />

          {!matterId && (
            <SharedGridStateIconButton
              gridStateKey="vaultExportsGridStates"
              module={ModuleList.modules.importExport.name}
              section={ModuleList.modules.importExport.sections.vaultExport}
            />
          )}
        </>
      }
    />
  );
}
